import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';

// Shared renderer for the public lead form's inputs. Used live on
// PublicLeadFormPage and as a disabled, non-interactive preview inside the
// staff form builder — keep it free of page-level concerns (submit button,
// honeypot, branding/theme, header) so both hosts stay in control of those.
//
// Props contract:
//   fields    LeadFieldDef[] — custom field definitions (form.fields)
//   values    { first_name, last_name, email, phone, notes, custom: {key: value} }
//   onChange  (patch) => void — called with a PARTIAL of `values`; the host
//             shallow-merges it (custom arrives as the full replacement object)
//   form      form settings object ({ show_notes, ... }); only show_notes is
//             read here, as a fallback when `showNotes` is not passed
//   showNotes bool — render the optional notes textarea (overrides form)
//   disabled  bool — disable every input (preview mode)
const LeadFormRenderer = ({
  fields = [],
  values = {},
  onChange = () => {},
  form = null,
  showNotes,
  disabled = false,
}) => {
  const custom = values.custom || {};
  const setCore = (name, v) => onChange({ [name]: v });
  const setCustom = (key, v) => onChange({ custom: { ...custom, [key]: v } });
  const notesVisible = showNotes !== undefined ? Boolean(showNotes) : Boolean(form?.show_notes);

  const renderField = (field, idx) => {
    const key = field.key || `field-${idx}`;

    if (field.type === 'heading') {
      // Display-only section heading — no value, never required.
      return (
        <Box key={key} sx={{ pt: idx === 0 ? 0 : 1, overflowWrap: 'break-word' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {field.label}
          </Typography>
          {field.help_text && (
            <Typography variant="caption" color="text.secondary">
              {field.help_text}
            </Typography>
          )}
        </Box>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <Box key={key}>
          <FormControlLabel
            control={
              <Checkbox
                checked={custom[field.key] === true}
                required={field.required}
                disabled={disabled}
                onChange={(e) => setCustom(field.key, e.target.checked)}
              />
            }
            label={field.required ? `${field.label} *` : field.label}
          />
          {field.help_text && (
            <FormHelperText sx={{ mt: -0.5, ml: 4 }}>{field.help_text}</FormHelperText>
          )}
        </Box>
      );
    }

    if (field.type === 'multiselect') {
      const selected = Array.isArray(custom[field.key]) ? custom[field.key] : [];
      const labelId = `lead-field-${key}-label`;
      return (
        <FormControl key={key} fullWidth required={field.required} disabled={disabled}>
          <InputLabel id={labelId}>{field.label}</InputLabel>
          <Select
            labelId={labelId}
            label={field.label}
            multiple
            value={selected}
            onChange={(e) => {
              const v = e.target.value;
              setCustom(field.key, typeof v === 'string' ? v.split(',') : v);
            }}
            renderValue={(vals) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {vals.map((v) => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Box>
            )}
          >
            {(field.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox size="small" checked={selected.includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>
          {field.help_text && <FormHelperText>{field.help_text}</FormHelperText>}
        </FormControl>
      );
    }

    if (field.type === 'select') {
      return (
        <TextField
          key={key}
          select
          label={field.label}
          required={field.required}
          fullWidth
          disabled={disabled}
          value={custom[field.key] ?? ''}
          onChange={(e) => setCustom(field.key, e.target.value)}
          placeholder={field.placeholder || undefined}
          helperText={field.help_text || undefined}
        >
          {!field.required && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          {(field.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // text / textarea / number / email / phone / date all render as TextField
    // variants distinguished only by the input type + multiline flags.
    const inputType =
      field.type === 'number'
        ? 'number'
        : field.type === 'email'
          ? 'email'
          : field.type === 'phone'
            ? 'tel'
            : field.type === 'date'
              ? 'date'
              : 'text';
    return (
      <TextField
        key={key}
        label={field.label}
        required={field.required}
        fullWidth
        disabled={disabled}
        type={inputType}
        multiline={field.type === 'textarea'}
        minRows={field.type === 'textarea' ? 2 : undefined}
        InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
        placeholder={field.placeholder || undefined}
        helperText={field.help_text || undefined}
        value={custom[field.key] ?? ''}
        onChange={(e) => setCustom(field.key, e.target.value)}
      />
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="First name"
          required
          fullWidth
          disabled={disabled}
          value={values.first_name ?? ''}
          onChange={(e) => setCore('first_name', e.target.value)}
          data-testid="public-first-name-input"
        />
        <TextField
          label="Last name"
          fullWidth
          disabled={disabled}
          value={values.last_name ?? ''}
          onChange={(e) => setCore('last_name', e.target.value)}
        />
      </Box>
      <TextField
        label="Email"
        type="email"
        required
        fullWidth
        disabled={disabled}
        value={values.email ?? ''}
        onChange={(e) => setCore('email', e.target.value)}
        data-testid="public-email-input"
      />
      <MuiTelInput
        label="Phone (optional)"
        fullWidth
        defaultCountry="US"
        forceCallingCode
        disabled={disabled}
        value={values.phone ?? ''}
        onChange={(value) => setCore('phone', value)}
      />
      {fields.map(renderField)}
      {notesVisible && (
        <TextField
          label="Anything you'd like us to know?"
          fullWidth
          multiline
          minRows={2}
          disabled={disabled}
          value={values.notes ?? ''}
          onChange={(e) => setCore('notes', e.target.value)}
          data-testid="public-notes-input"
        />
      )}
    </>
  );
};

export default LeadFormRenderer;
